/**
 * Persistencia del par de claves del dispositivo (T017; implementation-notes 002 §4).
 *
 * El `CryptoKeyPair` se guarda tal cual en IndexedDB (`idb-keyval`, base `fermin-device`, store
 * `keys`): las claves no extraíbles son objetos estructurados clonables, así que la clave privada
 * nunca sale de WebCrypto ni en el navegador ni en disco. Nada de esto va a `localStorage` ni a
 * `sessionStorage`.
 *
 * El acceso pasa por la interfaz `DeviceKeyStore` para que las pruebas (jsdom no implementa
 * `indexedDB`) inyecten un store en memoria con `setDeviceKeyStore(createMemoryKeyStore())`.
 */
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

export const DEVICE_DB_NAME = 'fermin-device';
export const DEVICE_STORE_NAME = 'keys';
/** Clave única dentro del store: solo hay un par de claves por instalación. */
export const DEVICE_KEY_PAIR_KEY = 'key-pair';

export interface DeviceKeyStore {
  get(): Promise<CryptoKeyPair | undefined>;
  set(pair: CryptoKeyPair): Promise<void>;
  clear(): Promise<void>;
}

function isCryptoKey(value: unknown): value is CryptoKey {
  if (typeof CryptoKey !== 'undefined' && value instanceof CryptoKey) {
    return true;
  }
  // Realm distinto (p. ej. jsdom + WebCrypto de Node): se acepta por forma.
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'algorithm' in value &&
    'usages' in value
  );
}

/** `true` si el valor leído del almacenamiento tiene la forma de un `CryptoKeyPair`. */
export function isCryptoKeyPair(value: unknown): value is CryptoKeyPair {
  return (
    typeof value === 'object' &&
    value !== null &&
    'privateKey' in value &&
    'publicKey' in value &&
    isCryptoKey(value.privateKey) &&
    isCryptoKey(value.publicKey) &&
    value.privateKey.type === 'private' &&
    value.publicKey.type === 'public'
  );
}

/** Store real sobre IndexedDB. La base se abre en el primer acceso, no al importar el módulo. */
export function createIdbKeyStore(): DeviceKeyStore {
  let store: UseStore | undefined;
  const openStore = (): UseStore => {
    store ??= createStore(DEVICE_DB_NAME, DEVICE_STORE_NAME);
    return store;
  };
  return {
    async get() {
      const value: unknown = await get(DEVICE_KEY_PAIR_KEY, openStore());
      // Un valor corrupto o de una versión anterior se trata como ausencia de clave.
      return isCryptoKeyPair(value) ? value : undefined;
    },
    set(pair) {
      return set(DEVICE_KEY_PAIR_KEY, pair, openStore());
    },
    clear() {
      return del(DEVICE_KEY_PAIR_KEY, openStore());
    },
  };
}

/** Store en memoria: pruebas unitarias y entornos sin IndexedDB. Se pierde al recargar. */
export function createMemoryKeyStore(): DeviceKeyStore {
  let pair: CryptoKeyPair | undefined;
  return {
    get: () => Promise.resolve(pair),
    set(next) {
      pair = next;
      return Promise.resolve();
    },
    clear() {
      pair = undefined;
      return Promise.resolve();
    },
  };
}

let activeStore: DeviceKeyStore | undefined;

/** Store activo; por defecto IndexedDB. */
export function getDeviceKeyStore(): DeviceKeyStore {
  activeStore ??= createIdbKeyStore();
  return activeStore;
}

/** Sustituye el store (pruebas). `null` vuelve al store por defecto. */
export function setDeviceKeyStore(store: DeviceKeyStore | null): void {
  activeStore = store ?? undefined;
}
